import { WorkflowBuildingAnimation } from '@/components/ChatV2/WorkflowBuildingAnimation'
import { TagManagementDropdown } from '@/components/Tags/TagManagementDropdown'
import { TagPill } from '@/components/Tags/TagPill'
import { EditableTitle } from '@/components/ui/EditableTitle'
import type { BreadcrumbItem, PanelContent } from '@/components/ui/Panel'
import { TopBar, TopBarToggleSwitch } from '@/components/ui/TopBar'
import { RetryValidationProvider } from '@/contexts/RetryValidationContext'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { useGetUserAdminStatusQuery } from '@/lib/features/user/userApi'
import { useGetWorkflowQuery, useUpdateWorkflowMutation } from '@/lib/features/workflow/workflowApi'
import { motion } from 'framer-motion'
import Image from 'next/image'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { NodeExecution } from 'sprites-types/api/v2/workflows'
import { useWorkflowActions } from './hooks/useWorkflowActions'
import PromptSuggestions from './PromptSuggestions'
import WorkflowResults, { useWorkflowResultsActions } from './Results/WorkflowResults'

import FlowchartViewer from '@/components/Flowchart/FlowchartViewer'
type Mode = 'results' | 'workflow'

interface WorkflowPanelProps {
  onOpenPanel?: (content: PanelContent, breadcrumbs: BreadcrumbItem[]) => void
}

export function WorkflowPanel({ onOpenPanel }: WorkflowPanelProps) {
  const [mode, setMode] = useState<Mode>('workflow')
  const previousHasResultsRef = useRef(false)
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false)
  const tagDropdownRef = useRef<HTMLDivElement>(null)

  // Get workflow context directly
  const { context, updateSelectedNodes, updateWorkflowRef, workflowState, sessionId } =
    useCopilotContext()

  // Extract values from context
  const contextWorkflowId = context.workflowRef
  const selectedNodeId = context.nodeId
  const selectedNodeIds = context.selectedNodeIds || []

  // Example usage of latestResults:
  // - Display final workflow outputs in a summary panel
  // - Show aggregated results from the last completed workflow execution
  // - Provide quick access to the most recent workflow results
  // - Enable comparison between different workflow executions

  // Fetch workflow using RTK Query
  const { data: workflow } = useGetWorkflowQuery(contextWorkflowId!, {
    skip: !contextWorkflowId,
  })

  // No preview ref needed in results/workflow split

  // Automatically switch to workflow mode when building starts
  React.useEffect(() => {
    if (workflowState.isBuilding) {
      setMode('workflow')
    }
  }, [workflowState.isBuilding])

  // Determine which workflow to show:
  // 1. If we have a workflow object from context, use that
  // 2. Otherwise use the workflowId from context
  const workflowToShow = contextWorkflowId || null

  // Create a setSelectedNodes-compatible function for context
  const setSelectedNodesCompat = useCallback(
    (updater: React.SetStateAction<string[]>) => {
      if (typeof updater === 'function') {
        updateSelectedNodes(updater(selectedNodeIds))
      } else {
        updateSelectedNodes(updater)
      }
    },
    [selectedNodeIds, updateSelectedNodes]
  )

  // Memoize stable values to prevent unnecessary rerenders
  const workflowData = useMemo(() => workflow?.data, [workflow?.data])
  const executionId = useMemo(
    () => workflowState.executionResult?.executionId,
    [workflowState.executionResult?.executionId]
  )
  const hasResults = useMemo(
    () =>
      Boolean(
        workflowState.executionResult?.outputs && workflowState.executionResult.outputs.length > 0
      ),
    [workflowState.executionResult?.outputs]
  )

  const { data: adminStatus } = useGetUserAdminStatusQuery()
  const [updateWorkflow] = useUpdateWorkflowMutation()

  // Handle workflow rename
  const handleRenameWorkflow = useCallback(
    async (newName: string) => {
      if (!contextWorkflowId) return

      try {
        const result = await updateWorkflow({
          workflowRef: contextWorkflowId,
          config: { name: newName },
          sessionId: sessionId || undefined,
        }).unwrap()

        // Update context with new workflow version if it changed
        // The backend creates a new version when updating a versioned workflow
        const newWorkflowRef = `workflow_${result.metadata.id}:${result.metadata.currentVersion}`
        if (newWorkflowRef !== contextWorkflowId) {
          updateWorkflowRef(newWorkflowRef)
        }

        toast.success('Workflow renamed successfully')
      } catch (error: any) {
        console.error('Failed to rename workflow:', error)
        const errorMessage =
          error?.data?.error || error?.error || 'Failed to rename workflow. Please try again.'
        toast.error(errorMessage)
        throw error
      }
    },
    [contextWorkflowId, updateWorkflow, sessionId, updateWorkflowRef]
  )

  // Auto-switch to results when results become available after execution
  useEffect(() => {
    // Only switch if we transitioned from no results to has results
    if (hasResults && !previousHasResultsRef.current && mode === 'workflow') {
      const timer = setTimeout(() => {
        setMode('results')
      }, 300)
      previousHasResultsRef.current = true
      return () => clearTimeout(timer)
    }

    // Reset the flag when results are cleared
    if (!hasResults && previousHasResultsRef.current) {
      previousHasResultsRef.current = false
    }
  }, [hasResults, mode])

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target as Node) &&
        isTagDropdownOpen
      ) {
        setIsTagDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isTagDropdownOpen])

  // Get workflow actions using the hook
  const workflowActions = useWorkflowActions({
    mode,
    workflowRef: contextWorkflowId || undefined,
    isAdmin: adminStatus?.isAdmin ?? false,
    onOpenTags: () => setIsTagDropdownOpen((prev) => !prev),
    currentTags: workflow?.metadata?.tags || [],
    workflowId: workflow?.metadata?.id,
    isBookmarked: workflow?.metadata?.isBookmarked ?? false,
  })

  // Process workflow executions for results actions
  const processedExecutions = useMemo(() => {
    if (!context.workflowExecutions) {
      return []
    }

    const executions = Object.values(context.workflowExecutions).map((execution) => {
      // Get all node executions for this workflow execution
      const allNodeExecutions = Object.values(execution.nodeExecutions)

      // Filter based on outputNodeExecutionIds if available
      let filteredNodes: NodeExecution[]
      if (execution.outputNodeExecutionIds && execution.outputNodeExecutionIds.length > 0) {
        const outputIdsSet = new Set(execution.outputNodeExecutionIds)
        filteredNodes = allNodeExecutions.filter((exec) => outputIdsSet.has(exec.executionId))
      } else {
        // Show all node executions if no filter specified
        filteredNodes = allNodeExecutions
      }

      return {
        executionId: execution.executionId,
        workflowRef: execution.workflowRef,
        workflowConfig: execution.workflowConfig,
        nodes: filteredNodes,
        createdAt: execution.createdAt,
      }
    })

    // Sort by creation time (newest first)
    executions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return executions
  }, [context.workflowExecutions])

  // Get results actions using the hook
  const resultsActions = useWorkflowResultsActions({
    workflow: workflowData,
    executionId,
    hasResults,
    processedExecutions,
  })

  // Create the mode toggle
  const modeToggle = (
    <TopBarToggleSwitch<Mode>
      mode={mode}
      onModeChange={(newMode) => setMode(newMode)}
      options={[
        { value: 'workflow', label: 'Flow View' },
        { value: 'results', label: 'Results' },
      ]}
    />
  )

  // Show empty state if no workflow to display
  if (!workflowToShow && !workflow) {
    return (
      <div className="relative flex h-full flex-col overflow-hidden rounded-t-[20px]">
        <div className="absolute inset-0">
          <motion.div className="absolute inset-0" />

          {/* Floating stars */}
          {[...Array(4)].map((_, i) => {
            const size = Math.random() * 40 + 30
            const positions = [
              { left: '10%', top: '15%' },
              { right: '15%', top: '20%' },
              { left: '20%', bottom: '25%' },
              { right: '10%', bottom: '15%' },
            ]
            return (
              <motion.div
                key={i}
                className="pointer-events-none absolute opacity-40"
                style={{
                  width: size,
                  height: size,
                  ...positions[i],
                }}
                animate={{
                  y: [0, -20, 0],
                  x: [0, Math.random() * 10 - 5, 0],
                  rotate: [0, 360],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 8 + Math.random() * 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 1.5,
                }}
              >
                <Image
                  src="https://res.cloudinary.com/dwx3u2p1d/image/upload/v1754442059/hero-left-star_e1qnhi.png"
                  alt=""
                  className="h-full w-full object-contain"
                  fill
                  style={{ filter: 'hue-rotate(20deg) brightness(1.2)' }}
                />
              </motion.div>
            )
          })}

          {/* Floating clouds */}
          {[...Array(2)].map((_, i) => {
            const size = Math.random() * 120 + 80
            const positions = [
              { left: '5%', top: '30%' },
              { right: '5%', bottom: '30%' },
            ]
            return (
              <motion.div
                key={`cloud-${i}`}
                className="pointer-events-none absolute opacity-20"
                style={{
                  width: size,
                  height: size * 0.6,
                  ...positions[i],
                }}
                animate={{
                  y: [0, -15, 0],
                  x: [0, Math.random() * 15 - 7.5, 0],
                  scale: [1, 1.03, 1],
                }}
                transition={{
                  duration: 12 + Math.random() * 6,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 3,
                }}
              >
                <Image
                  src="https://res.cloudinary.com/dwx3u2p1d/image/upload/v1754442066/hero-left_wzizjg.png"
                  alt=""
                  className="h-full w-full object-contain"
                  fill
                  style={{ filter: 'hue-rotate(10deg) brightness(1.1) opacity(0.7)' }}
                />
              </motion.div>
            )
          })}
        </div>

        {/* Placeholder content */}
        <div className="relative z-10 flex h-full items-center justify-center">
          <motion.div
            className="flex flex-col items-center justify-center text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            {workflowState.isBuilding ? (
              // Show workflow building animation
              <div className="rounded-2xl border border-white/20 bg-white/40 p-8 text-center shadow-lg backdrop-blur-sm">
                <WorkflowBuildingAnimation message={workflowState.buildState.buildMessage} />
              </div>
            ) : (
              // Show prompt suggestions when not building
              <PromptSuggestions />
            )}
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col rounded-t-[20px]">
      <TopBar
        leftActions={[...workflowActions, ...(mode === 'results' ? resultsActions : [])]}
        animated={true}
        animationType="stagger"
        animationProps={{ duration: 0.2, staggerDelay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        centerContent={
          (workflow?.data?.name || workflow?.metadata?.name) && (
            <motion.div
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2"
            >
              <EditableTitle
                title={workflow.data?.name || workflow.metadata?.name || ''}
                onSave={handleRenameWorkflow}
              />
              {workflow.metadata.tags && workflow.metadata.tags.length > 0 && (
                <div className="flex gap-1">
                  {workflow.metadata.tags.map((tag) => (
                    <TagPill
                      key={tag.id}
                      name={tag.name}
                      isGlobal={tag.workspaceId === null}
                      readonly
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )
        }
        rightContent={hasResults ? modeToggle : undefined}
      />

      {/* Content Area */}
      <div className="relative h-full overflow-hidden">
        {/* Workflow Mode */}
        <motion.div
          key="workflow"
          className="absolute inset-0"
          initial={false}
          animate={{
            opacity: mode === 'workflow' ? 1 : 0,
            x: mode === 'workflow' ? 0 : -20,
          }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
          style={{
            pointerEvents: mode === 'workflow' ? 'auto' : 'none',
          }}
        >
          <div className="relative h-full">
            {contextWorkflowId && context.nodeExecutions ? (
              <RetryValidationProvider
                workflowRef={contextWorkflowId}
                nodeExecutions={context.nodeExecutions}
              >
                <FlowchartViewer
                  workflowId={workflowToShow}
                  selectedNodeId={selectedNodeId}
                  setSelectedNodes={setSelectedNodesCompat}
                  onOpenPanel={onOpenPanel}
                  workflowConfig={workflow?.data}
                  nodeExecutions={context.nodeExecutions || undefined}
                  workflowExecutionId={context.workflowExecutionId}
                />
              </RetryValidationProvider>
            ) : (
              <FlowchartViewer
                workflowId={workflowToShow}
                selectedNodeId={selectedNodeId}
                setSelectedNodes={setSelectedNodesCompat}
                onOpenPanel={onOpenPanel}
                workflowConfig={workflow?.data}
                nodeExecutions={context.nodeExecutions || undefined}
                workflowExecutionId={context.workflowExecutionId}
              />
            )}
          </div>
        </motion.div>

        {/* Results Mode */}
        <motion.div
          key="results"
          className="absolute inset-0"
          initial={false}
          animate={{
            opacity: mode === 'results' ? 1 : 0,
            x: mode === 'results' ? 0 : 20,
          }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
          style={{
            pointerEvents: mode === 'results' ? 'auto' : 'none',
          }}
        >
          <div className="relative h-full">
            <WorkflowResults
              workflow={workflow?.data}
              enablePanelView={!!onOpenPanel}
              onOpenPanel={onOpenPanel}
            />
          </div>
        </motion.div>
      </div>

      {/* Tag Management Dropdown */}
      <div className="absolute left-12 top-16 z-30">
        {contextWorkflowId && workflow?.metadata && (
          <TagManagementDropdown
            ref={tagDropdownRef}
            isOpen={isTagDropdownOpen}
            workflowId={contextWorkflowId}
            workspaceId={workflow.metadata.workspaceId}
            currentTags={workflow.metadata.tags || []}
          />
        )}
      </div>
    </div>
  )
}
